<template>
  <f7-sheet
    :opened="opened"
    class="request-return-sheet"
    backdrop
    @sheet:closed="onClosed"
    @update:opened="(value) => emit('update:opened', value)"
  >
    <f7-toolbar>
      <div class="left">
        <f7-link sheet-close>{{ t('general.close') }}</f7-link>
      </div>
      <div class="title">{{ t('item.return.sheetTitle') }}</div>
      <div class="right" />
    </f7-toolbar>

    <f7-page-content>
      <f7-block class="request-return-sheet__intro">
        <p>{{ t('item.return.intro') }}</p>
      </f7-block>

      <f7-block-title>{{ t('item.return.reasonLabel') }}</f7-block-title>
      <div class="request-return-sheet__chips">
        <f7-chip
          v-for="option in reasonOptions"
          :key="option.value"
          :text="option.label"
          :outline="reason !== option.value"
          class="request-return-sheet__chip"
          :class="{ 'request-return-sheet__chip--selected': reason === option.value }"
          @click="pickReason(option.value)"
        ></f7-chip>
      </div>

      <f7-block-title>{{ t('item.return.qtyLabel') }}</f7-block-title>
      <f7-block class="request-return-sheet__qty">
        <f7-stepper
          :value="qty"
          :min="1"
          :max="maxQty"
          small
          fill
          raised
          :disabled="submitting"
          @stepper:change="onQtyChange"
        ></f7-stepper>
        <span class="request-return-sheet__qty-hint">
          {{ t('item.return.qtyHint', { max: maxQty }) }}
        </span>
      </f7-block>

      <!-- THE OPTIONAL SENTENCE. Same flat, always-visible field the shop-tip
           sheet beside this one uses — no "+" disclosure, because there is no
           live-answer real estate to protect here. -->
      <f7-block class="request-return-sheet__field">
        <label class="request-return-sheet__label" :for="noteFieldId">
          {{ t('item.return.noteLabel') }}
        </label>
        <textarea
          :id="noteFieldId"
          v-model="note"
          class="request-return-sheet__textarea"
          :placeholder="t('item.return.notePlaceholder')"
          :maxlength="RETURN_NOTE_MAX"
          :disabled="submitting"
          rows="2"
          dir="auto"
        />
      </f7-block>

      <!-- THE PHOTO. Same contract as the answer flow's optional photo
           (POST /filepond → a single-use token in `photo_token`), lazily
           imported for the same reason OptionalNote defers it: nothing here
           should join the bundle of a screen that never opens this sheet. -->
      <f7-block class="request-return-sheet__field">
        <span class="request-return-sheet__label">{{ t('item.return.photoLabel') }}</span>
        <avatar-file-pond-uploader
          ref="uploader"
          :accepted-file-types="ACCEPTED_TYPES"
          max-file-size="5MB"
          :placeholder-text="t('item.return.photoPlaceholder')"
          @click="pickPhoto"
          @update:model-value="onToken"
          @upload-error="onUploadError"
          @remove="onPhotoRemoved"
        ></avatar-file-pond-uploader>
        <p v-if="uploadError" class="request-return-sheet__error" role="alert">{{ uploadError }}</p>
      </f7-block>

      <!-- One message region for the client-side "pick a reason first" check
           and every server refusal, so two failures can never stack on screen
           — the same rule the shop-tip sheet keeps. -->
      <f7-block v-if="errorMessage" class="request-return-sheet__error">
        <p role="alert">{{ errorMessage }}</p>
      </f7-block>
    </f7-page-content>

    <!-- Outside the scroller, on purpose — see SupplierSuggestionSheet.vue for
         the full story (an unreachable submit inside Telegram on iOS). -->
    <template #fixed>
      <div class="sheet-fixed-footer">
        <f7-button
          fill
          large
          :disabled="submitting"
          @click="submit"
        >
          <f7-preloader
            v-if="submitting"
            size="20"
            class="margin-end-half"
          ></f7-preloader>
          {{ t('item.return.submit') }}
        </f7-button>
      </div>
    </template>
  </f7-sheet>
</template>

<script setup>
/**
 * RequestReturnSheet — «أرجّع الصنف», the member's post-delivery return door
 * (returns-desk P3-A part A; BE→FE brief `2026-09-17-member-return-request.md`).
 *
 * A DELIVERED line whose carton has taken delivery confirmation can ask to
 * come back: a reason, how many pieces (of the line's own qty), and an
 * optional note and photo. `ItemSheets.vue` owns eligibility
 * (`canRequestReturn`, utils/supplierReturn.js) and mounts this as its one
 * page-level instance, matching the SupplierSuggestionSheet pattern this
 * mirrors throughout.
 *
 * The three refusals: 403 means the door was offered on a line that has since
 * moved (collected into another state is impossible from DELIVERED, but the
 * carton can still close under her, or a return can open from the OTHER
 * side — the leader's own «مش زي ما طلب» door) — this emits `stale` and gets
 * out of the way, exactly as the shop-tip sheet's forbidden branch does. 409
 * means a return is already open (a second tap) and 422 means the body itself
 * — both carry the backend's own Arabic sentence and stay on screen next to
 * the fields she can change.
 */
import { computed, defineAsyncComponent, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from '@/composables/useToast';
import OrderBoxRepository from '@/repositories/OrderBoxRepository.js';
import { ORDER_BOX_ITEM_RETURN_FORBIDDEN } from '@/services/orderBoxItem.service.js';
import {
  POST_DELIVERY_RETURN_REASONS,
  RETURN_NOTE_MAX,
  returnReasonLabelKey,
} from '@/utils/supplierReturn.js';

defineOptions({ name: 'RequestReturnSheet' });

const props = defineProps({
  opened: {
    type: Boolean,
    default: false,
  },
  // The line she is returning. Null while the host has nothing selected — the
  // sheet cannot submit in that state.
  item: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:opened', 'submitted', 'stale']);

const { t } = useI18n({ useScope: 'global' });
const { showError } = useToast();

/** See OptionalNote.vue's docblock for why this MUST stay lazy: several
 *  eager `component:` routes touch order lines, and none of them should carry
 *  filepond + its plugins + its CSS in their initial chunk. */
const AvatarFilePondUploader = defineAsyncComponent(
  () => import('@/components/common/AvatarFilePondUploader.vue'),
);

// HEIC deliberately absent — same reasoning as OptionalNote/IssueCard: iPhones
// shoot it by default and the backend would rather refuse than store a format
// nothing can display.
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const uid = useId();
const noteFieldId = computed(() => `request-return-note-${uid}`);

const reasonOptions = computed(() =>
  POST_DELIVERY_RETURN_REASONS.map((value) => ({
    value,
    label: t(returnReasonLabelKey(value)),
  })),
);

const reason = ref(null);
const qty = ref(1);
const note = ref('');
const photoToken = ref('');
const submitting = ref(false);
const errorMessage = ref(null);
const uploadError = ref(null);
const uploader = ref(null);

/** 1..the line's own qty — the backend's own ceiling is narrower still (no
 *  more than what an earlier return on the line has not already claimed), and
 *  a refusal past that renders its own 422 sentence rather than being guessed
 *  here. */
const maxQty = computed(() => {
  const raw = Number(props.item?.qty);
  return Number.isInteger(raw) && raw > 0 ? raw : 1;
});

function pickReason(value) {
  reason.value = value;
  // A refusal was about the previous shape of the form; keeping it on screen
  // while she changes that shape reads as though the change was rejected.
  errorMessage.value = null;
}

function onQtyChange(value) {
  const clamped = Math.min(Math.max(Number(value) || 1, 1), maxQty.value);
  qty.value = clamped;
}

/** Reset to a clean sheet every time it opens — this is one page-level
 *  instance serving every eligible row, never a per-row instance. */
watch(
  () => props.opened,
  (isOpen) => {
    if (!isOpen) return;
    reason.value = null;
    qty.value = maxQty.value;
    note.value = '';
    photoToken.value = '';
    submitting.value = false;
    errorMessage.value = null;
    uploadError.value = null;
  },
);

function onClosed() {
  emit('update:opened', false);
}

/** The uploader emits `click`; opening the OS picker is the parent's call —
 *  the same seam OptionalNote uses to avoid wrapping this in a sheet of its
 *  own. */
function pickPhoto() {
  if (submitting.value) return;
  uploadError.value = null;
  uploader.value?.triggerFilePond?.();
}

function onToken(serverId) {
  photoToken.value = serverId || '';
  if (photoToken.value) uploadError.value = null;
}

function onPhotoRemoved() {
  uploadError.value = null;
  photoToken.value = '';
}

/** An upload failure is NOT a submit failure — the message surfaces inline
 *  and every other control stays live, so she can still send the return
 *  without a photo. */
function onUploadError(error) {
  uploadError.value =
    (typeof error === 'string' ? error : error?.body || error?.main || error?.message) || null;
}

async function submit() {
  if (submitting.value) return;

  const itemId = props.item?.id;
  if (itemId == null) return;

  if (!reason.value) {
    errorMessage.value = t('item.return.reasonRequired');
    // Announced, not merely rendered — the submit sits in a pinned footer
    // reachable from any scroll position, same reasoning as the shop-tip sheet.
    showError(errorMessage.value);
    return;
  }

  submitting.value = true;
  errorMessage.value = null;

  try {
    const updated = await OrderBoxRepository.requestReturn(
      props.item?.orderBoxId ?? null,
      itemId,
      {
        reason: reason.value,
        qty: qty.value,
        note: note.value,
        photoToken: photoToken.value,
      },
    );
    emit('submitted', updated);
    emit('update:opened', false);
  } catch (error) {
    if (error?.code === ORDER_BOX_ITEM_RETURN_FORBIDDEN) {
      emit('stale', { message: error.detail || t('item.return.errors.forbidden'), error });
      emit('update:opened', false);
      return;
    }

    const message = error?.detail || t('item.return.errors.generic');
    errorMessage.value = message;
    showError(message);
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
/*
  Definite height, same reasoning as SupplierSuggestionSheet.vue beside this
  file: a definite height needs no `sheet-scroll` rescue, and the unit stays a
  PERCENTAGE of the app root (never vh/dvh) so Telegram's WebView sizing
  cannot strand the scroller. Kept in step with the fixed footer's height via
  the same two custom properties that sheet declares.
*/
.request-return-sheet {
  height: 88%;
  --sheet-fixed-footer-h: 76px;
  --f7-page-content-extra-padding-bottom: var(--sheet-fixed-footer-h);
}

.request-return-sheet__intro p {
  margin: 0;
  font-size: var(--poolit-font-size-sm);
  line-height: 1.55;
  color: var(--poolit-text-secondary);
}

.request-return-sheet__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 16px 8px;
}

.request-return-sheet__chip--selected {
  --f7-chip-bg-color: var(--poolit-primary-500);
  color: #fff;
}

.request-return-sheet__qty {
  display: flex;
  align-items: center;
  gap: 12px;
}

.request-return-sheet__qty-hint {
  font-size: var(--poolit-font-size-xs);
  color: var(--poolit-text-muted);
}

.request-return-sheet__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.request-return-sheet__label {
  font-size: var(--poolit-font-size-sm);
  font-weight: var(--poolit-font-weight-medium);
  color: var(--poolit-text-primary);
}

/* Logical properties throughout, so RTL needs no override block. */
.request-return-sheet__textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid var(--poolit-border, #e2e5e3);
  border-radius: var(--poolit-radius-md);
  background: var(--poolit-surface, #fff);
  color: var(--poolit-text-primary);
  font-family: inherit;
  font-size: var(--poolit-font-size-md);
  line-height: 1.55;
  text-align: start;
  resize: vertical;
  min-height: 62px;
}

.request-return-sheet__textarea:focus-visible {
  outline: none;
  border-color: var(--poolit-primary-500);
}

.request-return-sheet__error p {
  margin: 0;
  font-size: var(--poolit-font-size-sm);
  line-height: 1.5;
  color: var(--poolit-danger, #d1453b);
}
</style>
